/**
 * Keyframe State Thumbnails
 *
 * Module-level cache of isolated object captures per keyframe — plus the Base
 * state (no keyframe applied) — used by the keyframe card strip in the viewport
 * overlay (mount-keyframe-context.tsx).
 *
 * Capture strategy (spec: docs-internal/KEYFRAME_EDITOR_STATES.md):
 * Captures the keyframe OWNER's subtree via trace-viewer's `captureObjectThumbnail`
 * (isolated offscreen RTT, no whole-scene render, no live mutation).
 *
 * Two capture paths:
 *  - **Current-pose** (`captureKeyframeThumbnail`): the named keyframe is already
 *    selected/settled live, so we just capture the current pose.
 *  - **Posed background fill** (`generateMissingThumbnails`): for keyframes the user
 *    hasn't visited, we pose each one OFF-SCREEN via a synchronous `goTo(name, false)`
 *    + restore inside `captureObjectThumbnail`'s pose hook. This does NOT navigate the
 *    live selection — the active keyframe is snapshotted and restored value-exact
 *    within one synchronous window the user can't observe (no shuffle). This is the
 *    clean replacement for the removed background cycle, which navigated keyframes
 *    *asynchronously* on the live selection and could silently deselect them.
 *
 * Dedup: each cache entry stores the content HASH it was captured at; a capture is
 * skipped when the current hash matches (re-selecting an unchanged keyframe, or an
 * edit to an unrelated keyframe, is a no-op).
 */

import { captureObjectThumbnail } from 'trace-viewer/editor';
import { Properties, type Keyframes } from 'trace-viewer/behaviors';
import type { SceneObject } from 'trace-viewer/scene';
import { ThumbnailCache, fileToDataUrl } from './thumbnail-cache';

/** Sentinel for the Base state (no keyframe applied). */
export const BASE_STATE = null;

const THUMB_SIZE = 192;

/**
 * FIFO cap. Cache keys embed transform uniqueIds, which are new on every
 * scene load — without eviction, dataURLs (~20-100KB each) from previous
 * scenes would be pinned for the lifetime of the tab.
 */
const MAX_CACHE_ENTRIES = 64;

/** Cache keyed by `${containerUniqueId}::${keyframeName | __base__}` */
const _cache = new ThumbnailCache(MAX_CACHE_ENTRIES);

function cacheKey(behavior: Keyframes, name: string | null): string {
    return `${behavior.sceneObject.transform.uniqueId}::${name ?? '__base__'}`;
}

/**
 * Signature of one node's authored (keyframe-independent) appearance: its pose,
 * its active/enabled flag, AND its authored property declarations. All three can
 * be overridden by a keyframe, so for pose/active we read the AUTHORED value when
 * Properties is driving the override — otherwise the signature would drift with
 * the active keyframe instead of tracking Base.
 *
 * The active flag matters because toggling an object on/off changes what the
 * thumbnail depicts (a hidden object disappears) every bit as much as moving it.
 *
 * Property declarations matter because a Base edit to a non-transform property
 * (color, opacity, material, text, …) changes what the thumbnail depicts without
 * touching pose or active. They live in the authored `properties` array, which is
 * keyframe-independent (a keyframe overrides them via contributions, not by
 * rewriting the authored declarations), so reading it from `getData()` tracks
 * Base regardless of which keyframe is applied.
 */
function nodeSig(obj: SceneObject): string {
    const props = obj.getBehavior(Properties);

    // Pose: authored when a state drives it, else the live node (= authored at Base).
    let pose: string;
    const authored = props?.getAuthoredTransform();
    if (authored) {
        // `getAuthoredTransform().rotation` is always a Quaternion (never null) —
        // unlike the live node below, which may use Euler `rotation` when
        // `rotationQuaternion` is absent. So no Euler fallback is needed here.
        const { position: p, rotation: r, scale: s } = authored;
        pose = `${p.x},${p.y},${p.z}|${r.x},${r.y},${r.z},${r.w}|${s.x},${s.y},${s.z}`;
    } else {
        const t = obj.transform;
        const r = t.rotationQuaternion;
        pose = `${t.position.x},${t.position.y},${t.position.z}|` +
            `${r ? `${r.x},${r.y},${r.z},${r.w}` : `${t.rotation.x},${t.rotation.y},${t.rotation.z}`}|` +
            `${t.scaling.x},${t.scaling.y},${t.scaling.z}`;
    }

    // Active: authored when a state drives it (getAuthoredActive), else the live
    // resolved value (= authored at Base, where no override is in flight).
    const active = props?.getAuthoredActive() ?? obj.active;

    // Authored property declarations (name → value), the source of every
    // non-transform appearance edit at Base. `getData()` is the authored snapshot
    // (a keyframe's contribution doesn't rewrite it), so this is keyframe-stable.
    const declSig = props
        ? (props.getData().properties ?? [])
            .map((d) => `${d.name}=${typeof d.value === 'object' ? JSON.stringify(d.value) : String(d.value)}`)
            .join(',')
        : '';

    return `${pose}|a:${active ? 1 : 0}|p:${declSig}`;
}

/**
 * Signature of the Base pose of the whole captured subtree (container + every
 * descendant). The thumbnail depicts the entire subtree, and keyframe entries
 * target DESCENDANTS — so a Base edit usually moves a descendant, not the
 * container itself. Hashing only the container's own transform (as we used to)
 * missed those edits, leaving the Base thumbnail stale.
 */
export function getBaseSubtreeSignature(behavior: Keyframes): string {
    return baseSubtreeSig(behavior);
}

function baseSubtreeSig(behavior: Keyframes): string {
    const parts = [nodeSig(behavior.sceneObject)];
    for (const desc of behavior.sceneObject.getDescendants()) {
        parts.push(nodeSig(desc));
    }
    return parts.join(';');
}

/**
 * Content hash of what a card depicts, so we can skip a capture when nothing
 * changed.
 *
 * For Base (no keyframe applied): the Base pose of the entire subtree.
 *
 * For a keyframe: its authored entry definition PLUS the Base subtree signature.
 * Base-inheritance semantics mean a keyframe IS its diff from Base — anything it
 * doesn't override renders at the Base pose — so a Base change alters how every
 * keyframe looks. Folding the Base signature in marks all keyframe thumbnails
 * stale when Base moves.
 */
function contentHash(behavior: Keyframes, name: string | null): string {
    const base = baseSubtreeSig(behavior);
    if (name === null) return `base:${base}`;
    const def = behavior.getData().keyframes?.[name];
    return `kf:${def ? JSON.stringify(def) : ''}|${base}`;
}

/** Subscribe to thumbnail cache changes. Returns an unsubscribe function. */
export function onKeyframeThumbnailsChanged(callback: () => void): () => void {
    return _cache.subscribe(callback);
}

/** Get the cached thumbnail dataURL for a keyframe (or BASE_STATE), if any. */
export function getKeyframeThumbnail(behavior: Keyframes, name: string | null): string | undefined {
    return _cache.getDataUrl(cacheKey(behavior, name));
}

/** Drop the cached thumbnail for a keyframe so it shows a placeholder until recaptured. */
export function invalidateKeyframeThumbnail(behavior: Keyframes, name: string | null): void {
    _cache.delete(cacheKey(behavior, name));
}

/** Move a cached thumbnail when a keyframe is renamed. */
export function renameKeyframeThumbnail(behavior: Keyframes, oldName: string, newName: string): void {
    _cache.rename(cacheKey(behavior, oldName), cacheKey(behavior, newName));
}

/**
 * Seed a new keyframe's thumbnail from an existing one (e.g. on duplicate — the
 * copy is identical to its source, so it can show the same image immediately
 * instead of a placeholder). The source entry is copied, not moved, with a cleared
 * hash so the next freshness check re-captures the copy from its own content.
 */
export function duplicateKeyframeThumbnail(behavior: Keyframes, sourceName: string, copyName: string): void {
    _cache.copyWithStaleHash(cacheKey(behavior, sourceName), cacheKey(behavior, copyName));
}

/** True when the cached thumbnail for `name` is up to date with its current content. */
function isFresh(behavior: Keyframes, name: string | null): boolean {
    return _cache.isFresh(cacheKey(behavior, name), contentHash(behavior, name));
}

/**
 * Capture the keyframe OWNER (isolated to its own subtree, auto-framed) as the
 * thumbnail for `name`, in its CURRENT live pose. The caller is responsible for
 * the named keyframe actually being selected (we capture whatever state just
 * became active / settled), so the live nodes are already posed in it.
 *
 * Skips the capture when the cached thumbnail is already fresh for the current
 * content (re-selecting an unchanged keyframe, or an unrelated edit, is a no-op).
 */
export async function captureKeyframeThumbnail(behavior: Keyframes, name: string | null): Promise<void> {
    if (isFresh(behavior, name)) return;
    // Snapshot the hash BEFORE the async capture: it must describe what we're about
    // to capture, not whatever the content became after the await. Otherwise an edit
    // landing mid-capture would store the old image under the new hash → `isFresh`
    // reports a stale thumbnail as current.
    const hash = contentHash(behavior, name);
    const file = await captureObjectThumbnail(behavior.sceneObject, { size: THUMB_SIZE });
    if (!file) return;
    _cache.set(cacheKey(behavior, name), { dataUrl: await fileToDataUrl(file), hash });
}

/**
 * Background-fill thumbnails for every keyframe (+ Base) that's missing or stale,
 * WITHOUT navigating the live selection. Each target is posed off-screen via
 * `captureObjectThumbnail`'s synchronous pose hook (`goTo(name, false)` → restore
 * `goTo(activeAtStart, false)`), so the user's active keyframe is reverted
 * value-exact within a window they can't observe — no shuffle, no silent deselect.
 *
 * The caller (the keyframe context overlay) mounts only while a Keyframes
 * container is the resolved selection, so this isn't run on asset load for
 * scenes the user never edits. Pass an `AbortSignal` to cancel on unmount /
 * container change.
 */
export async function generateMissingThumbnails(behavior: Keyframes, signal?: AbortSignal): Promise<void> {
    const targets: Array<string | null> = [BASE_STATE, ...behavior.getKeyframeNames()];
    for (const name of targets) {
        if (signal?.aborted) return;
        if (isFresh(behavior, name)) continue;

        // Pose hook: apply the target keyframe instantly, restore the keyframe that
        // was active when THIS ITERATION started. `activeAtStart` is read per-iteration
        // (not hoisted) on purpose: if the user navigates between fill iterations, each
        // target's restore targets the keyframe active at the start of its own iteration
        // — hoisting it out of the loop would reintroduce a selection race.
        // goTo(_, false) is synchronous in EFFECT (instant contributions apply before its
        // Promise settles — see the pose contract on captureObjectThumbnail), so the whole
        // apply→capture→restore stays in one no-yield window. We swallow the returned
        // Promise's rejection: the transform is already applied/reverted synchronously by
        // the time it could reject, so a late rejection (e.g. behavior torn down mid-fill)
        // is only unhandled-rejection noise, not a stuck selection.
        const activeAtStart = behavior.getActiveKeyframe();
        const ignore = () => {};
        const pose = name === activeAtStart
            ? undefined // already posed live → current-pose capture (cheaper)
            : () => {
                behavior.goTo(name, false).catch(ignore);
                return () => { behavior.goTo(activeAtStart, false).catch(ignore); };
            };

        // Snapshot the hash before the capture await (see captureKeyframeThumbnail).
        const hash = contentHash(behavior, name);
        const file = await captureObjectThumbnail(behavior.sceneObject, { size: THUMB_SIZE, pose });
        if (signal?.aborted) return;
        if (!file) continue;
        const dataUrl = await fileToDataUrl(file);
        if (signal?.aborted) return; // signal may have fired during fileToDataUrl
        _cache.set(cacheKey(behavior, name), { dataUrl, hash });
    }
}

